import classNames from "classnames"

import LayoutItem from "../Layouts/LayoutItem"

import TextButton from "@components/elements/V2/Buttons/TextButton"
import ClickableCardLink from "@components/elements/V2/ClickableCardLink"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import Typography from "@components/elements/V2/Typography"
import Video from "@components/experiments/Video"
import { Color } from "@constants/V2/color"
import {
  getAccentColor,
  getBackgroundColorClass,
  getGradientFromColorClass,
  getGradientToColorClass,
} from "@utils/V2/color"
import React from "react"

type Props = {
  heading1: string
  heading2?: string
  mobileHeading1?: string
  mobileHeading2?: string
  ctaText?: string
  ctaLink?: string | YocoCom.YocoLink
  primaryBackgroundColor: Color
  secondaryBackgroundColor?: Color
  imageUrl?: string
  imageAlt?: string
  mobileImageUrl?: string
  videoUrl?: string
  mobileGridOrder?: number
}

const ArticleBanner = ({
  heading1,
  heading2,
  mobileHeading1,
  mobileHeading2,
  ctaText,
  ctaLink,
  primaryBackgroundColor,
  secondaryBackgroundColor,
  imageUrl,
  imageAlt,
  mobileImageUrl,
  videoUrl,
  mobileGridOrder,
  ...props
}: Props) => {
  const hasCTA = !!(ctaText && ctaLink)
  const hasMedia = !!(videoUrl || imageUrl || mobileImageUrl)

  const primaryAccentColor = getAccentColor(primaryBackgroundColor)
  const secondaryAccentColor = getAccentColor(
    secondaryBackgroundColor ?? primaryBackgroundColor
  )

  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as="div"
      animateOnScroll
      className={classNames(
        "relative col-span-4 flex w-full overflow-hidden rounded-10 ring-1 ring-inset ring-charcoal/20 sm:col-span-4 sm:flex-row sm:items-center md:col-span-6 lg:col-span-12",
        hasCTA && "group cursor-pointer",
        secondaryBackgroundColor
          ? [
              "bg-gradient-to-r to-50% lg:to-60%",
              getGradientFromColorClass(primaryBackgroundColor),
              getGradientToColorClass(secondaryBackgroundColor),
            ]
          : getBackgroundColorClass(primaryBackgroundColor)
      )}
      {...props}
    >
      <div className="flex flex-1 flex-col gap-20 p-24 sm:gap-24 sm:py-32 lg:pl-40">
        <div className="flex flex-col">
          {mobileHeading1 && (
            <Typography
              element="h3"
              text={mobileHeading1}
              font="grotesk"
              weight="medium"
              size="lead-md"
              color={primaryAccentColor}
              className="sm:hidden"
            />
          )}

          <Typography
            element="h3"
            text={heading1}
            font="grotesk"
            weight="medium"
            size="lead-md"
            color={primaryAccentColor}
            className={classNames({ "hidden sm:block": mobileHeading1 })}
          />

          {mobileHeading2 && (
            <Typography
              element="h3"
              text={mobileHeading2}
              font="grotesk"
              weight="medium"
              size="lead-md"
              color={secondaryAccentColor}
              className="sm:hidden"
            />
          )}

          {heading2 && (
            <Typography
              element="h3"
              text={heading2}
              font="grotesk"
              weight="medium"
              size="lead-md"
              color={secondaryAccentColor}
              className={classNames({ "hidden sm:block": mobileHeading2 })}
            />
          )}
        </div>

        {ctaText && ctaLink && (
          <TextButton
            style="icon"
            text={ctaText}
            icon={<ArrowIcon />}
            color={secondaryAccentColor}
            decorative
          />
        )}
      </div>

      {hasMedia && (
        <div className="flex justify-end">
          {videoUrl ? (
            <Video
              video={videoUrl}
              coverImage={imageUrl ?? mobileImageUrl ?? ""}
              ImageAlt={imageAlt}
              playbackMode="autoplay"
              aspectRatio="1:1"
              className="h-full w-full max-w-[220px]"
            />
          ) : (
            <>
              {imageUrl && (
                <GatsbyStoryblokImage
                  image={imageUrl}
                  alt={imageAlt || "Banner image"}
                  aspectRatio="1:1"
                  imageObjectFit="contain"
                  className={classNames("h-full w-full !max-w-[220px]", {
                    "hidden sm:block": mobileImageUrl,
                  })}
                />
              )}

              {mobileImageUrl && (
                <GatsbyStoryblokImage
                  image={mobileImageUrl}
                  alt={imageAlt || "Banner image"}
                  aspectRatio="1:1"
                  imageObjectFit="contain"
                  className={classNames("h-full w-full !max-w-[220px]", {
                    "sm:hidden": imageUrl,
                  })}
                />
              )}
            </>
          )}
        </div>
      )}

      {ctaText && ctaLink && (
        <ClickableCardLink link={ctaLink} ariaLabel={heading1 || ctaText} />
      )}
    </LayoutItem>
  )
}

export default ArticleBanner
